// ─── PURE DATA CONSTANTS AND UTILITY FUNCTIONS ───────────────────────────────
import { T } from "./themes";

export const MEALS      = ["Breakfast","Lunch","Dinner","Snack"];
export const MEAL_ICON  = { Breakfast:"☀️", Lunch:"🌤️", Dinner:"🌙", Snack:"🍎" };
// MEAL_COLOR is defined inside App() — reads T after Object.assign so theme is live.
// Do NOT export it here.

export const PERSONAS   = [
  { id:"Motivational", emoji:"🔥", color:T.yellow   },
  { id:"Balanced",     emoji:"⚖️", color:T.accent   },
  { id:"Critical",     emoji:"🎯", color:T.secondary },
];

// THEME_PREVIEW: accent colours shown in PersonaPicker so user sees what each
// mode will look like before selecting. Uses raw hex values not T — intentional,
// these are the constant identity colours of each mode, not the live theme.
export const PERSONA_PREVIEW = {
  Balanced:     { accent:"#1a6b8a", accentDark:"#0d4a63", label:"Balanced",     sub:"Clinical & Data-Driven"  },
  Critical:     { accent:"#a32d2d", accentDark:"#1c1c1e", label:"Critical",     sub:"Blunt, Aggressive, Adult" },
  Motivational: { accent:"#4a9e2e", accentDark:"#2d5a1b", label:"Motivational", sub:"High-Energy & Relentless" },
};

// ─── SIMPLE GOAL PRESETS ─────────────────────────────────────────────────────
// Used by App.js (onboarding wizard) and SettingsScreen.jsx (goal picker).
// Single source of truth — do not duplicate.
export const SIMPLE_GOAL_PRESETS = {
  lose:     "I want to lose body fat and get lean. I'm willing to be in a calorie deficit.",
  maintain: "I want to maintain my current weight and body composition.",
  bulk:     "I want to build muscle mass and increase strength. I'm willing to eat in a surplus.",
};

export const SEDENTARY_KW = ["office","desk","sedentary","sitting","seated","work from home","wfh","computer","9-5","nine to five"];
export const isSedentary  = d => SEDENTARY_KW.some(kw => d.toLowerCase().includes(kw));

// Open Food Facts: free, no API key, no rate limit.
// Used for BOTH barcode lookup AND natural-language text search (NLP tab).
// Text search:  /cgi/search.pl?search_terms=QUERY&json=1&...
// Barcode:      /api/v0/product/BARCODE.json
// Spoonacular has been removed — OPF is the sole external food data source.
// V45: search_terms (general search) -- broader than V44 product_name tag filter.
// V44 was too strict; "Chicken Breast" returned nothing. Country subdomain +
// unique_scans_n sort retained. Balance between recall and relevance.
export const OPF_SEARCH = (q, country) => {
  const sub = (country && country !== "world") ? country : "world";
  return `https://${sub}.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(q)}&search_simple=1&action=process&json=1&page_size=20&sort_by=unique_scans_n&fields=product_name,nutriments,serving_size,serving_quantity,product_quantity,image_front_small_url,unique_scans_n`;
};
// V43: barcode lookup also uses country subdomain
export const OPF_URL = (code, country) => {
  const sub = (country && country !== "world") ? country : "world";
  return `https://${sub}.openfoodfacts.org/api/v0/product/${code}.json`;
};
export const TODAY_ISO = () => {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

// ─── OPF SERVING SIZE EXTRACTOR ──────────────────────────────────────────────
// Attempts to extract a sensible pack/serving weight from OPF product fields.
// Returns { label, grams } or null if nothing usable is found.
// OPF fields checked in priority order:
//   serving_size   e.g. "30g", "1 serving (45g)", "2 biscuits (28g)"
//   serving_quantity  numeric grams, sometimes populated separately
//   product_quantity  e.g. "500g", "1kg" — use as pack size suggestion
export function opfServingSuggestion(product) {
  // Helper: extract first number followed by g/ml from a string
  const extractGrams = (str) => {
    if (!str) return null;
    // Match patterns like "30g", "(30g)", "30 g", "30ml"
    const m = String(str).match(/(\d+\.?\d*)\s*(?:g|ml)/i);
    return m ? Math.round(parseFloat(m[1])) : null;
  };

  // 1. serving_size field — most reliable
  if (product.serving_size) {
    const g = extractGrams(product.serving_size);
    if (g && g > 0 && g <= 2000) {
      return { label: `1 serving (${g}g)`, grams: g };
    }
  }
  // 2. serving_quantity — sometimes a plain number in grams
  if (product.serving_quantity) {
    const g = Math.round(parseFloat(product.serving_quantity));
    if (g && g > 0 && g <= 2000) {
      return { label: `1 serving (${g}g)`, grams: g };
    }
  }
  // 3. product_quantity — use as "full pack" suggestion
  if (product.product_quantity) {
    const g = extractGrams(product.product_quantity + "g");  // ensure "g" suffix
    const rawG = Math.round(parseFloat(product.product_quantity));
    const final = g || rawG;
    if (final && final > 0 && final <= 5000) {
      return { label: `Full pack (${final}g)`, grams: Math.min(final, 500) };
    }
  }
  return null;
}

// ─── DETERMINISTIC MATH ───────────────────────────────────────────────────────
export function calcTargets(weight, height, age, gender, multiplier, weightGoal, wfh) {
  const w = parseFloat(weight), h = parseFloat(height), a = parseInt(age);
  if (!w || !h || !a) return { calories:0, protein:0, carbs:0, fat:0, bmr:0, tdeeRaw:0, fibre:30, sugar:30, sodium:2300 };
  const bmr  = (10*w) + (6.25*h) - (5*a) + (gender==="M" ? 5 : -161);
  const act  = wfh ? 1.2 : Math.max(1.2, Math.min(1.9, multiplier));
  let tdee   = bmr * act;
  if      (weightGoal==="Lose Weight") tdee -= 500;
  else if (weightGoal==="Gain Muscle") tdee += 250;
  // Fibre: 14g per 1000 kcal (IOM guideline), min 25g, max 40g
  const fibre  = Math.round(Math.min(40, Math.max(25, (tdee / 1000) * 14)));
  return {
    calories: Math.round(tdee),
    protein:  Math.round((tdee*0.30)/4),
    carbs:    Math.round((tdee*0.45)/4),
    fat:      Math.round((tdee*0.25)/9),
    fibre,
    sugar:    30,    // UK/WHO guideline: free sugars ≤30g/day
    sodium:   2300,  // American Heart Association guideline (mg)
    bmr:      Math.round(bmr),
    tdeeRaw:  Math.round(bmr*act),
  };
}
export function clamp(v,mn,mx){ return Math.max(mn,Math.min(mx,v)); }
export function fmtDuration(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  if (m === 0) return sec + "s";
  return m + "m" + (sec > 0 ? " " + sec + "s" : "");
}
export function fmtDate(iso) {
  return new Date(iso+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
}
// getHours() = LOCAL time. Never use getUTCHours() — logs must map to local clock.
export function hourOf(ts) { return ts ? new Date(ts).getHours() : new Date().getHours(); }

// ─── BODY MEASUREMENTS HELPERS ──────────────────────────────────────────────
export const BODY_FIELDS = [
  { key:"weight_kg",    label:"Weight",     unit:"kg",  step:"0.1" },
  { key:"body_fat_pct", label:"Body Fat",   unit:"%",   step:"0.1" },
  { key:"chest_cm",     label:"Chest",      unit:"cm",  step:"0.5" },
  { key:"waist_cm",     label:"Waist",      unit:"cm",  step:"0.5" },
  { key:"hips_cm",      label:"Hips",       unit:"cm",  step:"0.5" },
  { key:"arms_cm",      label:"Arms",       unit:"cm",  step:"0.5" },
  { key:"thighs_cm",    label:"Thighs",     unit:"cm",  step:"0.5" },
  { key:"neck_cm",      label:"Neck",       unit:"cm",  step:"0.5" },
  { key:"shoulders_cm", label:"Shoulders",  unit:"cm",  step:"0.5" },
];

// ─── EXECUTIVE SUMMARY PRINT ──────────────────────────────────────────────────
export function printSummary({ email, targets, dayHistory, wfhMode, physiqueGoal, persona }) {
  const r7     = dayHistory.slice(-7);
  const avg7   = r7.length>0 ? Math.round(r7.reduce((s,d)=>s+d.calories,0)/r7.length) : 0;
  const fail   = avg7>targets.calories && targets.calories>0;
  const date   = new Date().toLocaleDateString("en-GB",{weekday:"long",year:"numeric",month:"long",day:"numeric"});
  const rows   = r7.length>0
    ? [...r7].reverse().map(d=>{const delta=d.calories-d.target,over=delta>0;return`<tr><td>${fmtDate(d.date)}</td><td>${d.calories}</td><td>${d.target}</td><td style="color:${over?"#c0392b":"#8ba870"};font-weight:900">${over?"+":""}${delta}</td></tr>`;}).join("")
    : `<tr><td colspan="4" style="text-align:center;color:#888">No history yet</td></tr>`;
  const html=`<!DOCTYPE html><html><head><title>NutriLithe Audit</title><style>
  body{font-family:'Courier New',monospace;background:#d1d1d1;margin:0;padding:24px;color:#1a1a1a}
  h1{font-family:Impact,'Arial Black';font-size:32px;letter-spacing:2px;text-transform:uppercase;margin:0 0 4px}
  .sub{font-size:11px;color:#4a4a4a;margin:0 0 24px}
  .card{background:#fff;border:3px solid #1a1a1a;padding:16px;margin-bottom:20px}
  .label{font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#888;margin:0 0 6px}
  .val{font-family:Impact;font-size:36px;letter-spacing:1px;margin:0}
  .status{font-family:Impact;font-size:22px;letter-spacing:2px;text-transform:uppercase;color:${fail?"#c0392b":"#8ba870"}}
  table{width:100%;border-collapse:collapse}th,td{border:2px solid #1a1a1a;padding:8px 10px;font-size:12px}
  th{background:#1a1a1a;color:#8ba870;text-transform:uppercase;letter-spacing:1px}
  @media print{body{background:#fff}}
  </style></head><body>
  <h1>NutriLithe Audit</h1>
  <p class="sub">${date} · ${email} · Physique: ${physiqueGoal||"not set"} · Coach: ${persona} · WFH: ${wfhMode?"ACTIVE":"OFF"}</p>
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-bottom:20px">
    <div class="card"><p class="label">Daily Target</p><p class="val" style="color:#8ba870">${targets.calories}<span style="font-size:16px"> kcal</span></p></div>
    <div class="card"><p class="label">7-Day Avg</p><p class="val" style="color:${avg7>targets.calories?"#c0392b":"#1a1a1a"}">${avg7}<span style="font-size:16px"> kcal</span></p></div>
    <div class="card"><p class="label">System Status</p><p class="status">${fail?"⚠ FAILING":"✓ PASSING"}</p></div>
  </div>
  <div class="card"><p class="label" style="margin:0 0 10px">7-Day Log</p>
    <table><thead><tr><th>Date</th><th>Consumed</th><th>Target</th><th>Delta</th></tr></thead>
    <tbody>${rows}</tbody></table></div>
  <div class="card" style="font-size:11px;color:#4a4a4a">
    BMR: ${targets.bmr||"—"} kcal (Mifflin-St Jeor) &nbsp;|&nbsp;
    TDEE pre-offset: ${targets.tdeeRaw||"—"} kcal &nbsp;|&nbsp;
    WFH: ${wfhMode?"Locked 1.2×":"Off"}
  </div>
  <p style="font-size:9px;color:#888888">Generated by Lithe · ${new Date().toISOString()}</p>
  <script>window.print();</script></body></html>`;
  // Blob URL approach: avoids document.write which breaks in Safari/popup blockers
  const blob = new Blob([html], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const w    = window.open(url, "_blank", "width=900,height=700");
  if (w) { w.addEventListener("load", () => URL.revokeObjectURL(url), { once:true }); }
  else    { URL.revokeObjectURL(url); }
}
